import { useEffect, useState } from 'react';

import { useDigitalNavigationLogsQuery } from '@/store/digital-navigation-log/digital-navigation-log.store';
import { useOilLogsQuery } from '@/store/oil-log/oil-log.store';
import { useSafetyLogsQuery } from '@/store/safety-log/safety-log.store';
import { useTrashLogsQuery } from '@/store/trash-log/trash-log.store';
import { useVessels } from '@/store/vessels/vessels.store';
import { formatDate } from '@/utils/formatDate';

import type { LogBookRoute } from './logbookRoutes';

/**
 * The same page size the history tables ask for, so the two share one react-query
 * entry: opening a book after the hub or the index costs no second request.
 */
const PAGE_LIMIT = 100;

/** How a book stands: how much is in it, and when it was last written. */
export interface LogBookStanding {
  loading: boolean;
  /** The newest entry's date, absent when the book has never been written. */
  latest?: string;
  /** Everything in the book, not just the page that was fetched. */
  count: number;
}

const utcDayKey = (date: Date): string => date.toISOString().slice(0, 10);

/**
 * The day an entry belongs to, in the form every log page's `?date=` takes.
 */
export const logDateParam = (iso: string): string => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : utcDayKey(date);
};

/**
 * When the book was last written, in the shortest form that still says it:
 * "Today", "Yesterday", and a date once it is older than that — a name for a day
 * only beats the date while the reader can still place it, and past yesterday
 * nobody can.
 *
 * Days are counted in UTC because that is the calendar the logs are kept in: an
 * entry is stored at UTC midnight and every log page reads and writes `?date=`
 * in UTC, so a local-day comparison would call one entry "yesterday" on one side
 * of the world and "today" on the other. The date is rendered from that same
 * day, not from the raw stamp, for the same reason.
 */
export const formatLastWritten = (iso: string): string => {
  const day = logDateParam(iso);
  if (!day) return '';

  const now = new Date();
  if (day === utcDayKey(now)) return 'Today';

  const yesterday = new Date(now);
  yesterday.setUTCDate(yesterday.getUTCDate() - 1);
  if (day === utcDayKey(yesterday)) return 'Yesterday';

  return formatDate(`${day}T00:00:00`);
};

/**
 * The newest of a set of dates. Computed here rather than trusting the list to
 * arrive newest-first: the endpoints take a page, not a sort.
 */
const newestDate = (dates: (string | null | undefined)[]): string | undefined => {
  let newest: string | undefined;
  let newestTime = -Infinity;
  for (const date of dates) {
    if (!date) continue;
    const time = new Date(date).getTime();
    if (Number.isNaN(time) || time <= newestTime) continue;
    newest = date;
    newestTime = time;
  }
  return newest;
};

/** The four books that read through an infinite list query all answer the same shape. */
const listStanding = (query: {
  data?: { pages: readonly { count: number; items: readonly { date: string }[] }[] };
  isPending: boolean;
}): LogBookStanding => {
  const pages = query.data?.pages ?? [];
  return {
    loading: query.isPending,
    latest: newestDate(pages.flatMap((page) => page.items.map((item) => item.date))),
    // The server's total, which is what the card is claiming — not how many rows
    // happen to have been paged in.
    count: pages[0]?.count ?? 0,
  };
};

/**
 * The daily log has no query hook of its own — the vessel store fetches it
 * imperatively, exactly as `DailyLogHistory` does.
 */
const useDailyStanding = (): LogBookStanding => {
  const getDailyLogs = useVessels((st) => st.getDailyLogs);
  const [standing, setStanding] = useState<LogBookStanding>({ loading: true, count: 0 });

  useEffect(() => {
    let active = true;
    void getDailyLogs()
      .then((items) => {
        if (!active) return;
        setStanding({
          loading: false,
          latest: newestDate(items.map((log) => log.header?.date)),
          count: items.length,
        });
      })
      .catch(() => {
        // Nothing to claim about a book we could not read; it reads as unwritten.
        if (active) setStanding({ loading: false, count: 0 });
      });
    return () => {
      active = false;
    };
  }, [getDailyLogs]);

  return standing;
};

const useNavigationStanding = (): LogBookStanding =>
  listStanding(useDigitalNavigationLogsQuery(PAGE_LIMIT));

const useTrashStanding = (): LogBookStanding => listStanding(useTrashLogsQuery(PAGE_LIMIT));

const useSafetyStanding = (): LogBookStanding => listStanding(useSafetyLogsQuery(PAGE_LIMIT));

const useOilStanding = (): LogBookStanding => listStanding(useOilLogsQuery(PAGE_LIMIT));

const STANDING_HOOKS: Record<string, () => LogBookStanding> = {
  'vessel-daily-log': useDailyStanding,
  'daily-navigation-log': useNavigationStanding,
  'trash-log': useTrashStanding,
  'safety-log': useSafetyStanding,
  'oil-log': useOilStanding,
};

/**
 * How one book stands.
 *
 * Each book reads from its own store, so the caller must render **one component
 * per book** and keep that component's `book` fixed for its lifetime — a list
 * keyed by `book.path` does exactly that. It must also only be called for a book
 * the viewer can open: every request behind it would come back 403 on a book the
 * plan or the role withholds.
 */
export const useLogBookStanding = (book: LogBookRoute): LogBookStanding =>
  STANDING_HOOKS[book.path]();
